/**
 * React Context Provider for OpenTelemetry trace propagation
 *
 * Provides trace context to the React component tree, allowing components
 * to start/end traces and access the current trace ID.
 */

import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';

import {logger} from '../logger';

import {telemetryConfig} from './config';
import {tracer} from './tracer';
import {TraceContext} from './types';

/**
 * Context value provided to consumers
 */
interface TraceContextValue {
  /** Current active trace ID, or null if no trace is active */
  currentTraceId: string | null;

  /** Current active trace context, or null if no trace is active */
  currentTrace: TraceContext | null;

  /** Start a new trace with the given operation name */
  startTrace: (operationName: string) => TraceContext | null;

  /** End the current active trace */
  endTrace: () => void;

  /** Check if telemetry is enabled */
  isEnabled: boolean;
}

/**
 * React Context for trace propagation
 */
const OtelTraceContext = createContext<TraceContextValue | null>(null);

/**
 * Props for TraceProvider component
 */
interface TraceProviderProps {
  children: React.ReactNode;
}

/**
 * TraceProvider component
 *
 * Wraps the application (or part of it) to provide trace context.
 * Should be placed high in the component tree, typically at the app root.
 *
 * Usage:
 * ```tsx
 * <TraceProvider>
 *   <App />
 * </TraceProvider>
 * ```
 */
export const TraceProvider: React.FC<TraceProviderProps> = ({children}) => {
  const [currentTrace, setCurrentTrace] = useState<TraceContext | null>(null);

  /**
   * Start a new trace
   */
  const startTrace = useCallback((operationName: string): TraceContext | null => {
    if (!telemetryConfig.enabled) {
      return null;
    }

    try {
      const trace = tracer.startTrace(operationName);
      setCurrentTrace(trace);
      return trace;
    } catch (error) {
      if (telemetryConfig.debug || __DEV__) {
        logger.warn('OTEL TraceContext', 'Failed to start trace', {error});
      }
      return null;
    }
  }, []);

  /**
   * End the current trace
   */
  const endTrace = useCallback(() => {
    if (!telemetryConfig.enabled) {
      return;
    }

    try {
      tracer.endTrace();
      setCurrentTrace(null);
    } catch (error) {
      if (telemetryConfig.debug || __DEV__) {
        logger.warn('OTEL TraceContext', 'Failed to end trace', {error});
      }
    }
  }, []);

  /**
   * Memoized context value to avoid unnecessary re-renders
   */
  const contextValue = useMemo<TraceContextValue>(
    () => ({
      currentTraceId: currentTrace?.traceId || null,
      currentTrace,
      startTrace,
      endTrace,
      isEnabled: telemetryConfig.enabled,
    }),
    [currentTrace, startTrace, endTrace],
  );

  return <OtelTraceContext.Provider value={contextValue}>{children}</OtelTraceContext.Provider>;
};

/**
 * Hook to access trace context
 *
 * Usage:
 * ```tsx
 * const { startTrace, endTrace, currentTraceId } = useTrace();
 *
 * useEffect(() => {
 *   startTrace('my-operation');
 *   return () => endTrace();
 * }, []);
 * ```
 *
 * @returns Trace context value
 * @throws Error if used outside of TraceProvider
 */
export function useTrace(): TraceContextValue {
  const context = useContext(OtelTraceContext);

  if (!context) {
    throw new Error('useTrace must be used within a TraceProvider');
  }

  return context;
}

/**
 * Optional hook that doesn't throw if used outside TraceProvider
 * Returns null if no provider is found
 *
 * Useful for components that may or may not be wrapped in TraceProvider
 */
export function useTraceOptional(): TraceContextValue | null {
  return useContext(OtelTraceContext);
}
